/**
 * PWR journey steps — WHEN each step runs. WHAT gets written → pwrWebJobRouting.ts.
 *
 * READ THIS FILE TOP TO BOTTOM (matches patient journey):
 *   §1 SiteSelection.tsx  — Initiated on mount; Complete after site Confirm (zip/NSC deferred)
 *   §2 Layout.tsx         — Complete on WF-2/3 no-site outcome pages
 *   §3 ContactInfo        — WF-1 Complete after contact submit
 *   §4 Workflow rules     — journey-type gates in pwrWebJobRouting.ts (used by §1–§3)
 */
import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { Field, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { JOURNEYS } from '../../constants';
import { RootState, store } from '../../store/store';
import {
  isDapProject,
  isEnrollmentDnq,
  isWf23NoSiteReferralOutcome,
  postPwrCompleteJob,
  postPwrInitiatedRecord,
  shouldDeferPwrCompleteUntilContact,
  shouldSubmitPwrInitiatedOnSiteSelectionMount,
} from '../pwr/pwrWebJobRouting';

export type PwrJourneyStepsOptions = {
  websitePwrEnabled: boolean;
};

// ═══════════════════════════════════════════════════════════════════════════════
// §1  SITE SELECTION — SiteSelection.tsx
// ═══════════════════════════════════════════════════════════════════════════════

const usePwrJourneySteps = (options: PwrJourneyStepsOptions) => {
  const websitePwrEnabled = options.websitePwrEnabled;
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const localProtocols = (sitecoreContext as any)?.pwrLocalProtocols ?? {};
  const cdpEnrollment = useSelector((state: RootState) => state.cdpData);
  const screenerQualifiedProtocol = useSelector(
    (state: RootState) => state.screenerOutput?.pSec ?? null
  );
  const screenerInflare = useSelector((state: RootState) => state.screenerOutput?.FLA ?? null);
  const journeyType = cdpEnrollment?.journeyType;

  const hasInitiatedBeenSubmitted = useRef(false);

  /** WF-2/3 Initiated on site-selection mount. */
  const submitPwrInitiated = useCallback(async () => {
    if (
      !websitePwrEnabled ||
      !isDapProject() ||
      hasInitiatedBeenSubmitted.current ||
      !shouldSubmitPwrInitiatedOnSiteSelectionMount(journeyType)
    ) {
      return;
    }

    const initiatedRecordSaved = await postPwrInitiatedRecord({
      cdpData: cdpEnrollment,
      siteSettings,
      localProtocols,
      screenerQualifiedProtocol,
      screenerInflare,
    });
    if (initiatedRecordSaved) {
      hasInitiatedBeenSubmitted.current = true;
    }
  }, [
    cdpEnrollment,
    localProtocols,
    journeyType,
    screenerInflare,
    screenerQualifiedProtocol,
    siteSettings,
    websitePwrEnabled,
  ]);

  /**
   * PWR Complete after site Confirm — SiteSelection.tsx only (before CDP next-step fetch).
   * Site PR from selectedPlace.siteInfo. WF-1: no-op (Complete on contact).
   */
  const submitPwrOnSiteSelectionComplete = useCallback(
    async (selectedSiteInfo?: { enableSitePreRecruitment?: boolean } | null) => {
      if (
        !websitePwrEnabled ||
        !isDapProject() ||
        shouldDeferPwrCompleteUntilContact(journeyType)
      ) {
        return;
      }

      await postPwrCompleteJob({
        cdpData: cdpEnrollment,
        siteSettings,
        localProtocols,
        journeyType,
        evaluationContext: 'siteSelection',
        screenerQualifiedProtocol,
        screenerInflare,
        selectedSiteInfo,
      });
    },
    [
      cdpEnrollment,
      localProtocols,
      journeyType,
      screenerInflare,
      screenerQualifiedProtocol,
      siteSettings,
      websitePwrEnabled,
    ]
  );

  return { submitPwrInitiated, submitPwrOnSiteSelectionComplete };
};

export default usePwrJourneySteps;

// ═══════════════════════════════════════════════════════════════════════════════
// §2  OUTCOME PAGE — Layout.tsx (WF-2/3 NSA, NSAMC, NSC)
// ═══════════════════════════════════════════════════════════════════════════════

export const usePwrJourneyStepsOnOutcomePage = (
  route: any,
  options: PwrJourneyStepsOptions
): void => {
  const websitePwrEnabled = options.websitePwrEnabled;
  const router = useRouter();
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const localProtocols = (sitecoreContext as any)?.pwrLocalProtocols ?? {};
  const cdpEnrollment = useSelector((state: RootState) => state.cdpData);
  const screenerQualifiedProtocol = useSelector(
    (state: RootState) => state.screenerOutput?.pSec ?? null
  );
  const screenerInflare = useSelector((state: RootState) => state.screenerOutput?.FLA ?? null);
  const journeyType = cdpEnrollment?.journeyType;
  const hasOutcomePageJobBeenSubmitted = useRef(false);
  const isOutcomePageJobInFlight = useRef(false);

  useEffect(() => {
    const isOutcomePage = (route?.fields?.isOutcomePage as Field)?.value;

    if (
      !websitePwrEnabled ||
      !isDapProject() ||
      isOutcomePage !== true ||
      !router?.isReady ||
      hasOutcomePageJobBeenSubmitted.current ||
      isOutcomePageJobInFlight.current ||
      isEnrollmentDnq(cdpEnrollment) ||
      !isWf23NoSiteReferralOutcome(journeyType, cdpEnrollment?.patientReferralStatus)
    ) {
      return;
    }

    isOutcomePageJobInFlight.current = true;
    void postPwrCompleteJob({
      cdpData: { ...cdpEnrollment, nextStepUrl: router.asPath },
      siteSettings,
      localProtocols,
      journeyType,
      evaluationContext: 'outcomePage',
      screenerQualifiedProtocol,
      screenerInflare,
    }).then((completeJobSaved) => {
      isOutcomePageJobInFlight.current = false;
      if (completeJobSaved) {
        hasOutcomePageJobBeenSubmitted.current = true;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router?.isReady, router.asPath, cdpEnrollment, journeyType, websitePwrEnabled]);
};

// ═══════════════════════════════════════════════════════════════════════════════
// §3  CONTACT FORM — Adult/Caregiver/Minor ContactInfo (WF-1 Complete)
// ═══════════════════════════════════════════════════════════════════════════════

export const usePwrJourneyStepsOnContactForm = (options: PwrJourneyStepsOptions) => {
  const websitePwrEnabled = options.websitePwrEnabled;
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const localProtocols = (sitecoreContext as any)?.pwrLocalProtocols ?? {};
  const cdpEnrollment = useSelector((state: RootState) => state.cdpData);
  const screenerQualifiedProtocol = useSelector(
    (state: RootState) => state.screenerOutput?.pSec ?? null
  );
  const screenerInflare = useSelector((state: RootState) => state.screenerOutput?.FLA ?? null);

  const handlePwrAfterContactForm = useCallback(async () => {
    const cdpData = store.getState().cdpData ?? cdpEnrollment;

    if (
      !websitePwrEnabled ||
      !isDapProject() ||
      cdpData?.journeyType !== JOURNEYS.JOURNEY_01 ||
      isEnrollmentDnq(cdpData)
    ) {
      return;
    }

    await postPwrCompleteJob({
      cdpData,
      siteSettings,
      localProtocols,
      journeyType: cdpData.journeyType,
      screenerQualifiedProtocol,
      screenerInflare,
      evaluationContext: 'contactSubmit',
    });
  }, [
    cdpEnrollment,
    localProtocols,
    screenerInflare,
    screenerQualifiedProtocol,
    siteSettings,
    websitePwrEnabled,
  ]);

  return { handlePwrAfterContactForm };
};
